import { inject, Injectable } from '@angular/core';
import { AccountService, SearchSecurityItem, WideSearchComponent } from '@pinnakl/poems';
import { PinnaklSpinnerService } from '@pinnakl/shared/util-providers';

import { filter } from 'rxjs/operators';

import { Account } from '@pinnakl/poems/accounts/domain';
import { PoemsCommonDataStore } from '@pinnakl/poems/data-access';
import { SecurityDetailsComponentData } from '@pinnakl/poems/modals/security-details/domain';
import { SecurityMarketsStore } from '@pinnakl/securities/data-access';
import { DialogService } from 'primeng/dynamicdialog';

@Injectable()
export class NavBarService {
  private readonly dialogService = inject(DialogService);
  private readonly accountService = inject(AccountService);
  private readonly commonDataStore = inject(PoemsCommonDataStore);
  private readonly spinner = inject(PinnaklSpinnerService);

  private readonly securityMarketsStore = inject(SecurityMarketsStore);

  searchModalOpen(closeCb?: (event: SearchSecurityItem) => void): void {
    this.dialogService
      .open(WideSearchComponent, {
        width: '632px',
        height: '611px',
        contentStyle: { 'border-radius': '8px', padding: '16px' },
        showHeader: false,
      })
      .onClose.pipe(filter(v => !!v?.event))
      .subscribe(result => (closeCb ? closeCb(result.event) : this.openPositionSummaryDialog(result.event)));
  }

  private async openPositionSummaryDialog(item: SearchSecurityItem): Promise<void> {
    this.spinner.spin();
    this.commonDataStore.loadFxRates();
    try {
      const [accounts, securityDetails, { SecurityDetailsModalContainerComponent }] = await Promise.all([
        this.accountService.getAccounts(),
        this.securityMarketsStore.loadSecurityMarketDetails(item.securityMarketId),
        import('@pinnakl/poems/modals/security-details/feature'),
      ]);
      const componentData: SecurityDetailsComponentData = {
        ticker: securityDetails.market.ticker,
        selectedAccounts: [],
        availableAccounts: accounts.map((acc): Account => ({
          id: acc.id,
          clientId: acc.clientId,
          name: acc.name,
          accountCode: acc.accountCode,
          accountCodeLabel: acc.accountCode.toUpperCase(),
          orderOfImportance: acc.orderOfImportance,
          isPrimaryForReturns: acc.isPrimaryForReturns,
          pbAccounts: acc.pbAccounts,
          startDate: new Date().toISOString(),
          endDate: new Date().toISOString(),
        })),
        underlyingSecurityId: securityDetails.underlyingSecId?.toString(),
        isExposureSummaryDisabled: true,
        disableSearchSecurities: false,
        folderId: undefined,
        securityMarketId: item.securityMarketId.toString(),
      };
      this.dialogService
        .open(SecurityDetailsModalContainerComponent, {
          data: componentData,
          styleClass: 'security-details-modal',
          width: '1400px',
          position: 'top',
          draggable: true,
          modal: false,
          keepInViewport: true,
          closable: true,
          maskStyleClass: 'tw:z-[10001]',
          contentStyle: {
            'border-radius': '8px',
          },
          showHeader: false,
        })
        .onClose.subscribe(type => {
          if (type === 'searchSecurity') {
            this.searchModalOpen();
          }
        });
    } finally {
      this.spinner.stop();
    }
  }
}
